import React, { useEffect, useMemo, useState } from "react";
import { getCompanyDetails, getCompanyNames } from "../services/companyService";
import { toast } from "../utils/toast";
import "../styles/hierarchical-view.css";
import SearchableDropdown from "../components/ui/SearchableDropdown";
import { buildHierarchy } from "../components/hierarchy/hierarchyModel";
import HierarchyGraphView from "../components/hierarchy/HierarchyGraphView";
import HierarchyChartView from "../components/hierarchy/HierarchyChartView";
import HierarchyTreeView from "../components/hierarchy/HierarchyTreeView";

const VIEW_MODES = [
  { id: "graph", label: "Graph", icon: "fa fa-sitemap" },
  { id: "chart", label: "Chart", icon: "fa fa-th-large" },
  { id: "tree", label: "Tree View", icon: "fa fa-list-ul" },
];

const FunctionalHierarchyPage = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  // Text in the company box, and the company actually loaded. Kept apart so
  // focusing or typing in the box doesn't unload (and later refetch) the
  // whole hierarchy.
  const [selectedLabel, setSelectedLabel] = useState("");
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [selectedCompanyName, setSelectedCompanyName] = useState("");
  const [funcs, setFuncs] = useState([]);
  const [funcsLoading, setFuncsLoading] = useState(false);
  const [funcsError, setFuncsError] = useState(null);
  const [viewMode, setViewMode] = useState("graph");
  const didInitRef = React.useRef(false);

  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    (async () => {
      try {
        setLoading(true);
        const data = await getCompanyNames();
        setCompanies(Array.isArray(data) ? data : []);
      } catch (e) {
        toast.error(e?.message || "Failed to load companies");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const options = useMemo(() => {
    const seen = new Set();
    return (companies || []).reduce((acc, c) => {
      const label = [c.name, c.companyCode ? `(${c.companyCode})` : null]
        .filter(Boolean)
        .join(" ");
      if (!seen.has(label)) {
        acc.push({ label, id: c.company_id });
        seen.add(label);
      }
      return acc;
    }, []);
  }, [companies]);

  const selectedCompanyLabel = useMemo(
    () => options.find((o) => o.id === selectedCompanyId)?.label || "",
    [options, selectedCompanyId],
  );

  const companyOptions = useMemo(
    () => options.map((opt) => ({ label: opt.label, value: String(opt.id) })),
    [options],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Only load when a valid company is selected
      if (!selectedCompanyId) {
        setFuncs([]);
        setFuncsError(null);
        setFuncsLoading(false);
        setSelectedCompanyName("");
        return;
      }
      try {
        setFuncsLoading(true);
        setFuncsError(null);
        const details = await getCompanyDetails(selectedCompanyId);
        const rows = Array.isArray(details?.functions) ? details.functions : [];
        const normalized = rows.map((f) => ({
          ...f,
          function_id: Number(f?.function_id ?? f?.id),
          company_id: Number(f?.company_id ?? selectedCompanyId),
          parent_id:
            f?.parent_id == null || f?.parent_id === "" ?
              null
            : Number(f.parent_id),
          jobs:
            Array.isArray(f?.jobs) ? f.jobs
            : Array.isArray(f?.job) ? f.job
            : [],
        }));
        if (!cancelled) {
          setSelectedCompanyName(String(details?.company_name || ""));
          setFuncs(normalized.filter((f) => Number.isFinite(f.function_id)));
        }
      } catch (e) {
        if (!cancelled) setFuncsError(e?.message || "Failed to load functions");
      } finally {
        if (!cancelled) setFuncsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedCompanyId]);

  const selectedCompany = useMemo(() => {
    if (selectedCompanyName) {
      return {
        company_id: Number(selectedCompanyId),
        name: selectedCompanyName,
      };
    }
    return (
      companies.find(
        (c) => Number(c.company_id) === Number(selectedCompanyId),
      ) || null
    );
  }, [companies, selectedCompanyId, selectedCompanyName]);

  // Nested tree + rollup counts, shared by the chart and tree views.
  const hierarchy = useMemo(() => buildHierarchy(funcs), [funcs]);

  const clearSelection = () => {
    setSelectedLabel("");
    setSelectedCompanyId(null);
    setSelectedCompanyName("");
    setFuncs([]);
  };

  const hasFuncs = Array.isArray(funcs) && funcs.length > 0;

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="header-content">
          <div>
            <h2 className="page-title">Functional Hierarchy</h2>
            <p className="page-subtitle">
              Select a company to view its functional hierarchy
            </p>
          </div>
          <div className="roles-toolbar hierarchy-page-toolbar">
            <div
              className="roles-search"
              onBlur={() => setSelectedLabel(selectedCompanyLabel)}
            >
              <SearchableDropdown
                id="companyDropdown"
                className="search-input searchable-combobox"
                value={selectedLabel}
                options={companyOptions}
                placeholder="Search and select a company..."
                clearOnFocus
                onChange={(val) => {
                  setSelectedLabel(val);
                  // Picking an option, or typing a company's exact name, selects it.
                  const match = options.find((o) => o.label === val);
                  if (match && match.id !== selectedCompanyId) {
                    setSelectedCompanyName("");
                    setSelectedCompanyId(match.id);
                  }
                }}
              />
              <button
                type="button"
                className="secondary-btn sm"
                onClick={clearSelection}
                disabled={loading}
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="page-content">
        {loading && <div className="no-results">Loading companies...</div>}

        {!loading && options.length === 0 && (
          <div className="no-results">No companies found.</div>
        )}

        {!loading && options.length > 0 && !selectedCompanyId && (
          <div className="no-results">
            Select a company from the dropdown above.
          </div>
        )}

        {selectedCompanyId && (
          <>
            {funcsError && <div className="error-banner">{funcsError}</div>}
            {funcsLoading ?
              <div className="no-results">Loading functions...</div>
            : hasFuncs ?
              <>
                <div className="hierarchy-view-toggle" role="tablist">
                  {VIEW_MODES.map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      role="tab"
                      className={viewMode === m.id ? "active" : ""}
                      aria-selected={viewMode === m.id}
                      onClick={() => setViewMode(m.id)}
                      title={m.label}
                    >
                      <i className={m.icon} aria-hidden="true" />
                      {m.label}
                    </button>
                  ))}
                </div>
                {viewMode === "graph" && (
                  <HierarchyGraphView
                    funcs={funcs}
                    selectedCompany={selectedCompany}
                    selectedCompanyId={selectedCompanyId}
                  />
                )}
                {viewMode === "chart" && (
                  <HierarchyChartView
                    hierarchy={hierarchy}
                    selectedCompany={selectedCompany}
                  />
                )}
                {viewMode === "tree" && (
                  <HierarchyTreeView
                    hierarchy={hierarchy}
                    selectedCompany={selectedCompany}
                  />
                )}
              </>
            : <div className="no-results">
                No functions found for the selected company.
              </div>
            }
          </>
        )}
      </div>
    </div>
  );
};

export default FunctionalHierarchyPage;
